// Interacts with PropelAuth for token refresh, no direct backend API calls to Finn.
import { useAuthInfo } from '@propelauth/react';
import { useEffect, useState, useCallback, useRef } from 'react';
import { isDevelopment } from '../utils/env';

interface TokenRefreshOptions {
    // Time before expiry to refresh (in seconds) - default 5 minutes
    refreshThreshold?: number;
    // Whether to log refresh events to console (default: true in dev)
    enableLogging?: boolean;
}

// Utility to decode JWT payload
const decodeJwt = (token: string): any => {
    try {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(
            atob(base64)
                .split('')
                .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                .join('')
        );
        return JSON.parse(jsonPayload);
    } catch (error) {
        console.error('Error decoding token:', error);
        return null;
    }
};

// Hook to automatically refresh the token before it expires
export const useTokenRefresh = (options: TokenRefreshOptions = {}) => {
    const {
        refreshThreshold = 300, // 5 minutes by default
        enableLogging = isDevelopment(),
    } = options;

    const authInfo = useAuthInfo();
    const [lastRefreshTime, setLastRefreshTime] = useState<number>(Date.now());
    const refreshTimerRef = useRef<number | null>(null);

    // Add a ref to track the last focus event time for debouncing
    const lastFocusTimeRef = useRef<number>(0);
    const FOCUS_DEBOUNCE_MS = 2000; // Only refresh once per 2 seconds on focus

    const logMessage = useCallback((message: string, type: 'info' | 'error' = 'info') => {
        if (enableLogging) {
            if (type === 'error') {
                console.error(`[TokenRefresh] ${message}`);
            } else {
                console.log(`[TokenRefresh] ${message}`);
            }
        }
    }, [enableLogging]);

    const clearRefreshTimer = useCallback(() => {
        if (refreshTimerRef.current !== null) {
            window.clearTimeout(refreshTimerRef.current);
            refreshTimerRef.current = null;
        }
    }, []);

    const scheduleRefresh = useCallback(async (token: string) => {
        try {
            // Clear any existing timer
            clearRefreshTimer();

            // Decode token to get expiration
            const decodedToken = decodeJwt(token);
            if (!decodedToken || !decodedToken.exp) {
                logMessage('Invalid token or no expiration found');
                return;
            }

            // Calculate when token needs refresh (exp is in seconds)
            const expirationTime = decodedToken.exp * 1000; // Convert to milliseconds
            const refreshTime = expirationTime - (refreshThreshold * 1000);
            const now = Date.now();

            // If token is already expired or needs immediate refresh
            if (refreshTime <= now) {
                logMessage('Token needs immediate refresh');

                // Use authInfo.tokens.getAccessToken() to trigger a token refresh
                if (authInfo?.tokens) {
                    await authInfo.tokens.getAccessToken();
                    setLastRefreshTime(Date.now());
                    logMessage('Token refreshed immediately');
                } else {
                    logMessage('No tokens object available in authInfo for immediate refresh', 'error');
                }
                return;
            }

            // Schedule refresh
            const timeUntilRefresh = refreshTime - now;
            logMessage(`Token expires in ${Math.round((expirationTime - now) / 1000)}s, scheduling refresh in ${Math.round(timeUntilRefresh / 1000)}s`);

            refreshTimerRef.current = window.setTimeout(async () => {
                logMessage('Refreshing token');
                try {
                    // Use authInfo.tokens.getAccessToken() to trigger a token refresh
                    if (authInfo?.tokens) {
                        let success = false;
                        let attempts = 0;
                        const maxAttempts = 3;
                        while (attempts < maxAttempts && !success) {
                            attempts++;
                            try {
                                logMessage(`Attempting to refresh token (attempt ${attempts}/${maxAttempts})`);
                                await authInfo.tokens.getAccessToken();
                                success = true;
                                setLastRefreshTime(Date.now());
                                logMessage('Token refreshed successfully');
                            } catch (attemptError) {
                                logMessage(`Failed to refresh token on attempt ${attempts}/${maxAttempts}: ${attemptError}`, 'error');
                                if (attempts >= maxAttempts) {
                                    logMessage('Max refresh attempts reached. Token refresh failed definitively.', 'error');
                                    // TODO: Signal persistent failure to the UI (e.g., via context or event)
                                    // For now, this will result in the old token potentially expiring.
                                } else {
                                    const delay = Math.pow(2, attempts - 1) * 1000; // 1s, 2s
                                    logMessage(`Waiting ${delay}ms before next refresh attempt.`);
                                    await new Promise(resolve => setTimeout(resolve, delay));
                                }
                            }
                        }
                    } else {
                        logMessage('No tokens object available in authInfo for scheduled refresh', 'error');
                    }
                } catch (error) {
                    logMessage(`Unexpected error during token refresh scheduling/execution: ${error}`, 'error');
                }
            }, timeUntilRefresh);

        } catch (error) {
            logMessage(`Error scheduling token refresh: ${error}`, 'error');
        }
    }, [clearRefreshTimer, logMessage, refreshThreshold, authInfo?.tokens]);

    // Effect to handle token monitoring and dev-mode enhancements
    useEffect(() => {
        if (authInfo?.isLoggedIn && authInfo.accessToken) {
            logMessage(`Token found. LoggedIn: ${authInfo.isLoggedIn}. Scheduling refresh.`);
            scheduleRefresh(authInfo.accessToken);

            // In development mode, set up additional checks
            if (isDevelopment()) {
                const handleWindowFocus = async () => {
                    const now = Date.now();
                    if (now - lastFocusTimeRef.current < FOCUS_DEBOUNCE_MS) {
                        logMessage('Skipping token refresh - focus events debounced');
                        return;
                    }
                    lastFocusTimeRef.current = now;
                    logMessage('Window focused, attempting token refresh');

                    if (authInfo.tokens) {
                        try {
                            let success = false;
                            let attempts = 0;
                            const maxAttempts = 2; // Shorter for focus/online events
                            while (attempts < maxAttempts && !success) {
                                attempts++;
                                try {
                                    logMessage(`Attempting token refresh on focus/online (attempt ${attempts}/${maxAttempts})`);
                                    await authInfo.tokens.getAccessToken();
                                    success = true;
                                    setLastRefreshTime(Date.now());
                                    logMessage('Token refreshed on window focus/online');
                                } catch (attemptError) {
                                    logMessage(`Failed to refresh token on window focus/online (attempt ${attempts}/${maxAttempts}): ${attemptError}`, 'error');
                                    if (attempts >= maxAttempts) {
                                        logMessage('Max refresh attempts on focus/online reached.', 'error');
                                    } else {
                                        // No explicit delay/retry loop here for focus/online,
                                        // as these are opportunistic. Failure means it waits for next scheduled refresh.
                                    }
                                }
                            }
                        } catch (error) {
                            logMessage(`Error during focus/online token refresh: ${error}`, 'error');
                        }
                    } else {
                        logMessage('No tokens object for focus refresh.', 'error');
                    }
                };

                const handleOnline = async () => {
                    logMessage('Network connection restored, attempting token refresh');
                    if (authInfo.tokens) {
                        try {
                            await authInfo.tokens.getAccessToken();
                            setLastRefreshTime(Date.now());
                            logMessage('Token refreshed after coming online');
                        } catch (error) {
                            logMessage(`Failed to refresh token after coming online: ${error}`, 'error');
                        }
                    } else {
                        logMessage('No tokens object for online refresh.', 'error');
                    }
                };

                window.addEventListener('focus', handleWindowFocus);
                window.addEventListener('online', handleOnline);

                return () => {
                    clearRefreshTimer();
                    window.removeEventListener('focus', handleWindowFocus);
                    window.removeEventListener('online', handleOnline);
                };
            }
        } else {
            logMessage(`Not logged in or no access token. LoggedIn: ${authInfo?.isLoggedIn}. Token present: ${!!authInfo?.accessToken}. Clearing timer.`);
            clearRefreshTimer(); // Clear timer if logged out or no token
        }

        // Component cleanup for the main effect
        return () => {
            clearRefreshTimer();
        };
    }, [
        authInfo?.isLoggedIn,
        authInfo?.accessToken, // Reacts to token changes
        authInfo?.tokens, // Reacts to tokens object availability
        scheduleRefresh,
        clearRefreshTimer,
        logMessage, // Include if its identity changes, though useCallback should stabilize it
        isDevelopment, // If this can change at runtime, though unlikely for a const
        FOCUS_DEBOUNCE_MS, // Constant, but for completeness
    ]);

    // Return last refresh time for debugging/monitoring
    return { lastRefreshTime };
};

export default useTokenRefresh; 